import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import InfoHelper from './components/InfoHelper';
import 'leaflet/dist/leaflet.css';
import apiFetch from './api';
import L from 'leaflet';
import './CaseRegistrationOfficial.css';
import { setupMapTileLayers, MapLayerSwitcher, MapSearchBar } from './components/MapControls';
import { showModal } from './modalHelper';

const SECTIONS_LIST = [
  { id: 'sec-denunciante', title: '1. Denunciante & Autoridade' },
  { id: 'sec-vitima', title: '2. Identificação da Vítima' },
  { id: 'sec-fisicas', title: '3. Características & Sinais' },
  { id: 'sec-vestuario', title: '4. Vestuário & Cinotecnia K9' },
  { id: 'sec-mapa', title: '5. Cartografia Multi-Pontos' },
  { id: 'sec-comunicacoes', title: '6. Meios & Viatura' },
  { id: 'sec-saude', title: '7. Saúde, Autonomia & Medicação' },
  { id: 'sec-risco', title: '8. Matriz de Risco (15 Indicadores)' },
  { id: 'sec-condicional', title: '9. Circunstâncias Específicas' },
  { id: 'sec-diligencias', title: '10. Dinâmica & Diligências' }
];

const STORAGE_KEY = 'saria_form_oficial';

// Client-side masking/obfuscation to prevent casual DevTools inspection of sensitive PII
function maskDraft(data) {
  try {
    const jsonStr = JSON.stringify(data);
    const bytes = new TextEncoder().encode(jsonStr);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    sessionStorage.setItem(STORAGE_KEY, btoa(binary));
  } catch (e) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (_) {}
  }
}

function unmaskDraft() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    if (stored.startsWith('{')) return JSON.parse(stored);
    const binary = atob(stored);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const jsonStr = new TextDecoder().decode(bytes);
    return JSON.parse(jsonStr);
  } catch (e) {
    return {};
  }
}

function CaseRegistrationOfficial({ onCaseRegistered, onCaseSubmitted }) {
  const [formData, setFormData] = useState(() => unmaskDraft());

  // Debounced sessionStorage draft autosave (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      maskDraft(formData);
    }, 300);
    return () => clearTimeout(handler);
  }, [formData]);

  const [processing, setProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeSection, setActiveSection] = useState('sec-denunciante');

  // Map state and refs
  const mapRef = useRef(null);
  const lkpMarkerRef = useRef(null);
  const residenceMarkerRef = useRef(null);
  const relevantMarkersRef = useRef([]);
  const mapInitialized = useRef(false);
  const tileControllerRef = useRef(null);
  const [activeMapLayer, setActiveMapLayer] = useState('osm');

  // Multi-point management mode
  const [mapMarkerMode, setMapMarkerMode] = useState('lkp'); // 'lkp' | 'residence' | 'relevant'
  const [relevantPointLabel, setRelevantPointLabel] = useState('');
  const [relevantPointType, setRelevantPointType] = useState('Local Frequente');
  const [relevantPoints, setRelevantPoints] = useState(() => {
    const d = unmaskDraft();
    if (Array.isArray(d.Locais_Relevantes)) return d.Locais_Relevantes;
    if (typeof d.Locais_Relevantes_GeoJSON === 'string') {
      try { return JSON.parse(d.Locais_Relevantes_GeoJSON); } catch (_) { return []; }
    }
    return [];
  });

  // Calculate if victim is minor based on current formData
  const isMinor = useMemo(() => {
    const age = parseInt(formData.Idade_Exacta, 10);
    if (!Number.isNaN(age)) return age < 18;
    if (formData.Data_Nascimento) {
      const dob = new Date(formData.Data_Nascimento);
      if (!Number.isNaN(dob.getTime())) {
        const diff_ms = Date.now() - dob.getTime();
        const age_dt = new Date(diff_ms);
        return Math.abs(age_dt.getUTCFullYear() - 1970) < 18;
      }
    }
    return false;
  }, [formData.Idade_Exacta, formData.Data_Nascimento]);

  // Base tile layer switch
  const handleBaseLayerChange = (layerKey) => {
    setActiveMapLayer(layerKey);
    if (tileControllerRef.current) {
      tileControllerRef.current.switchLayer(layerKey);
    }
  };

  // Helper: update or place LKP marker
  const updateLkpMarker = useCallback((lat, lon) => {
    if (!mapRef.current) return;
    if (lkpMarkerRef.current) {
      lkpMarkerRef.current.setLatLng([lat, lon]);
    } else {
      const icon = L.divIcon({
        className: 'saria-map-marker',
        html: '<div class="marker-pin marker-pin-lkp"><span class="marker-text">LKP</span></div>',
        iconSize: [40, 36],
        iconAnchor: [20, 34]
      });
      lkpMarkerRef.current = L.marker([lat, lon], { icon }).addTo(mapRef.current);
      lkpMarkerRef.current.bindPopup('<b>Local de Desaparecimento (LKP / PLS)</b>');
    }
  }, []);

  // Helper: update or place Residence marker
  const updateResidenceMarker = useCallback((lat, lon) => {
    if (!mapRef.current) return;
    if (residenceMarkerRef.current) {
      residenceMarkerRef.current.setLatLng([lat, lon]);
    } else {
      const icon = L.divIcon({
        className: 'saria-map-marker',
        html: '<div class="marker-pin marker-pin-residence"><span class="marker-text">RES</span></div>',
        iconSize: [40, 36],
        iconAnchor: [20, 34]
      });
      residenceMarkerRef.current = L.marker([lat, lon], { icon }).addTo(mapRef.current);
      residenceMarkerRef.current.bindPopup('<b>Residência Habitual da Vítima</b>');
    }
  }, []);

  // Helper: synchronize relevant points markers
  const syncRelevantMarkers = useCallback((pointsList) => {
    if (!mapRef.current) return;
    relevantMarkersRef.current.forEach(m => {
      try { mapRef.current.removeLayer(m); } catch (_) {}
    });
    relevantMarkersRef.current = [];

    pointsList.forEach(pt => {
      const lat = parseFloat(pt.latitude);
      const lon = parseFloat(pt.longitude);
      if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
        const shortLabel = (pt.label || pt.tipo || 'PTO').substring(0, 8);
        const icon = L.divIcon({
          className: 'saria-map-marker',
          html: `<div class="marker-pin marker-pin-relevant"><span class="marker-text">${shortLabel}</span></div>`,
          iconSize: [48, 36],
          iconAnchor: [24, 34]
        });
        const m = L.marker([lat, lon], { icon }).addTo(mapRef.current);
        m.bindPopup(`<b>${pt.label || 'Ponto Relevante'}</b><br/>Tipo: ${pt.tipo || 'Geral'}<br/>${pt.address || ''}`);
        relevantMarkersRef.current.push(m);
      }
    });
  }, []);

  // Handle location chosen from search bar
  const handleLocationSelected = async (loc) => {
    const clat = loc.lat;
    const clon = loc.lon;
    if (!mapRef.current) return;
    const coordStr = `${clat.toFixed(6)}, ${clon.toFixed(6)}`;

    if (mapMarkerMode === 'lkp') {
      updateLkpMarker(clat, clon);
      mapRef.current.setView([clat, clon], 16, { animate: true });
      setFormData(prev => ({
        ...prev,
        Latitude: String(clat),
        Longitude: String(clon),
        Morada_Exacta_Coordenadas: coordStr,
        Local_Ultimo_Avistamento: prev.Local_Ultimo_Avistamento || loc.display_name || '',
        Freguesia: loc.freguesia || prev.Freguesia || '',
        Concelho: loc.concelho || prev.Concelho || '',
        Geocode_Level: loc.type === 'coordinates' ? 'manual_coords' : 'search_bar'
      }));
    } else if (mapMarkerMode === 'residence') {
      updateResidenceMarker(clat, clon);
      mapRef.current.setView([clat, clon], 16, { animate: true });
      setFormData(prev => ({
        ...prev,
        Residencia_Latitude: String(clat),
        Residencia_Longitude: String(clon),
        Residencia_Morada: loc.display_name || coordStr
      }));
    } else if (mapMarkerMode === 'relevant') {
      mapRef.current.setView([clat, clon], 16, { animate: true });
      const label = relevantPointLabel.trim() || relevantPointType || 'Ponto Relevante';
      const newPt = {
        id: 'pt_' + Date.now(),
        label: label,
        tipo: relevantPointType,
        latitude: clat,
        longitude: clon,
        address: loc.display_name || coordStr
      };
      setRelevantPoints(prev => {
        const updated = [...prev, newPt];
        setFormData(f => ({
          ...f,
          Locais_Relevantes: updated,
          Locais_Relevantes_GeoJSON: JSON.stringify(updated)
        }));
        syncRelevantMarkers(updated);
        return updated;
      });
      setRelevantPointLabel('');
    }
  };

  // Auto-fill GNR operator profile from localStorage on mount
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user'));
      if (u) {
        setFormData(prev => ({
          ...prev,
          GNR_Nome_Elemento: prev.GNR_Nome_Elemento || u.displayName || u.name || '',
          GNR_Posto_Elemento: prev.GNR_Posto_Elemento || u.posto || u.post || '',
          GNR_Unidade_Elemento: prev.GNR_Unidade_Elemento || u.unidade || u.unit || '',
          GNR_NIM_Elemento: prev.GNR_NIM_Elemento || u.nim || ''
        }));
      }
    } catch (_) {}
  }, []);

  // Initialize Leaflet Map
  useEffect(() => {
    if (!mapInitialized.current) {
      setTimeout(() => {
        const mapContainer = document.getElementById('mappicker');
        if (mapContainer && !mapRef.current) {
          mapInitialized.current = true;
          const initialCenter = formData.Latitude && formData.Longitude
            ? [parseFloat(formData.Latitude), parseFloat(formData.Longitude)]
            : [38.7223, -9.1393];

          mapRef.current = L.map('mappicker', { center: initialCenter, zoom: 12 });
          tileControllerRef.current = setupMapTileLayers(mapRef.current, 'osm');

          // Restore markers from draft if present
          if (formData.Latitude && formData.Longitude) {
            updateLkpMarker(parseFloat(formData.Latitude), parseFloat(formData.Longitude));
          }
          if (formData.Residencia_Latitude && formData.Residencia_Longitude) {
            updateResidenceMarker(parseFloat(formData.Residencia_Latitude), parseFloat(formData.Residencia_Longitude));
          }
          if (relevantPoints.length > 0) {
            syncRelevantMarkers(relevantPoints);
          }

          mapRef.current.on('click', async function(e) {
            const { lat: clat, lng: clon } = e.latlng;
            const coordStr = `${clat.toFixed(6)}, ${clon.toFixed(6)}`;

            if (mapMarkerMode === 'lkp') {
              updateLkpMarker(clat, clon);
              setFormData(prev => ({
                ...prev,
                Latitude: String(clat),
                Longitude: String(clon),
                Morada_Exacta_Coordenadas: coordStr,
                Geocode_Level: 'manual_map'
              }));
              try {
                const qs = new URLSearchParams({ lat: String(clat), lon: String(clon) });
                const resp = await fetch((process.env.REACT_APP_API_URL || '') + '/api/reverse-geocode?' + qs.toString());
                if (resp.ok) {
                  const payload = await resp.json();
                  if (payload && payload.success && payload.reverse) {
                    const rev = payload.reverse;
                    setFormData(prev => ({
                      ...prev,
                      Local_Ultimo_Avistamento: prev.Local_Ultimo_Avistamento || rev.display_name || '',
                      Freguesia: rev.freguesia || prev.Freguesia || '',
                      Concelho: rev.concelho || prev.Concelho || ''
                    }));
                  }
                }
              } catch (_) {}
            } else if (mapMarkerMode === 'residence') {
              updateResidenceMarker(clat, clon);
              setFormData(prev => ({
                ...prev,
                Residencia_Latitude: String(clat),
                Residencia_Longitude: String(clon),
                Residencia_Morada: coordStr
              }));
              try {
                const qs = new URLSearchParams({ lat: String(clat), lon: String(clon) });
                const resp = await fetch((process.env.REACT_APP_API_URL || '') + '/api/reverse-geocode?' + qs.toString());
                if (resp.ok) {
                  const payload = await resp.json();
                  if (payload && payload.success && payload.reverse && payload.reverse.display_name) {
                    setFormData(prev => ({
                      ...prev,
                      Residencia_Morada: payload.reverse.display_name
                    }));
                  }
                }
              } catch (_) {}
            } else if (mapMarkerMode === 'relevant') {
              const label = relevantPointLabel.trim() || relevantPointType || 'Ponto Relevante';
              const newPt = {
                id: 'pt_' + Date.now(),
                label: label,
                tipo: relevantPointType,
                latitude: clat,
                longitude: clon,
                address: coordStr
              };
              try {
                const qs = new URLSearchParams({ lat: String(clat), lon: String(clon) });
                const resp = await fetch((process.env.REACT_APP_API_URL || '') + '/api/reverse-geocode?' + qs.toString());
                if (resp.ok) {
                  const payload = await resp.json();
                  if (payload && payload.success && payload.reverse && payload.reverse.display_name) {
                    newPt.address = payload.reverse.display_name;
                  }
                }
              } catch (_) {}
              setRelevantPoints(prev => {
                const updated = [...prev, newPt];
                setFormData(f => ({
                  ...f,
                  Locais_Relevantes: updated,
                  Locais_Relevantes_GeoJSON: JSON.stringify(updated)
                }));
                syncRelevantMarkers(updated);
                return updated;
              });
              setRelevantPointLabel('');
            }
          });
        }
      }, 500);
    }

    // Scrollspy logic
    const handleScroll = () => {
      let current = SECTIONS_LIST[0].id;
      for (const section of SECTIONS_LIST) {
        const el = document.getElementById(section.id);
        if (el && window.scrollY >= (el.offsetTop - 160)) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      try {
        if (tileControllerRef.current) tileControllerRef.current.remove();
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      } catch (_) {}
    };
  }, [mapMarkerMode, relevantPointLabel, relevantPointType, updateLkpMarker, updateResidenceMarker, syncRelevantMarkers, formData.Latitude, formData.Longitude, formData.Residencia_Latitude, formData.Residencia_Longitude, relevantPoints]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 50, behavior: 'smooth' });
    }
  };

  // Form field change handler with automatic calculations
  const handleChange = (field, value) => {
    let v = value;
    const newForm = { ...formData };

    // Automatic age calculation from birth date
    if (field === 'Data_Nascimento' && v) {
      const dob = new Date(v);
      if (!Number.isNaN(dob.getTime())) {
        const diff_ms = Date.now() - dob.getTime();
        const age_dt = new Date(diff_ms);
        const calc_age = Math.abs(age_dt.getUTCFullYear() - 1970);
        newForm['Idade_Exacta'] = calc_age.toString();

        if (calc_age < 18) {
          newForm['Pessoa_Menor_Idade'] = 'Sim';
          newForm['Pessoa_Idosa'] = 'Não';
        } else {
          newForm['Pessoa_Menor_Idade'] = 'Não';
          newForm['Pessoa_Idosa'] = calc_age > 75 ? 'Sim' : 'Não';
        }
        if (calc_age > 80) {
          newForm['Falta_Autonomia'] = 'Sim';
        }
      }
    }

    // Direct age change calculation
    if (field === 'Idade_Exacta' && v) {
      const ageNum = parseInt(v, 10);
      if (!Number.isNaN(ageNum)) {
        if (ageNum < 18) {
          newForm['Pessoa_Menor_Idade'] = 'Sim';
          newForm['Pessoa_Idosa'] = 'Não';
        } else {
          newForm['Pessoa_Menor_Idade'] = 'Não';
          newForm['Pessoa_Idosa'] = ageNum > 75 ? 'Sim' : 'Não';
        }
        if (ageNum > 80) {
          newForm['Falta_Autonomia'] = 'Sim';
        }
      }
    }

    // Phone validation
    const phoneFields = ['Denunciante_Contacto', 'Denunciante_Telefone_Alternativo', 'Telemovel_Principal', 'Contacto_Telemovel'];
    if (phoneFields.includes(field)) {
      v = String(value || '').replace(/\D+/g, '').slice(0, 9);
      if (v !== '' && v.length !== 9) {
        setValidationErrors(prev => ({ ...prev, [field]: 'Deve ter 9 dígitos' }));
      } else {
        setValidationErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
      }
    } else {
      if (validationErrors[field] && value && String(value).trim() !== '') {
        setValidationErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
      }
    }

    newForm[field] = v;
    setFormData(newForm);
  };

  const handleCheckboxGroup = (field, checked, detailField) => {
    const newForm = { ...formData };
    newForm[field] = checked ? 'Sim' : 'Não';
    if (!checked && detailField) {
      newForm[detailField] = '';
    }
    setFormData(newForm);
  };

  // Center map on specific coordinates
  const handleRecenter = (lat, lon) => {
    if (mapRef.current && lat && lon) {
      mapRef.current.setView([parseFloat(lat), parseFloat(lon)], 16, { animate: true });
    }
  };

  // Remove LKP marker
  const handleRemoveLkp = () => {
    if (lkpMarkerRef.current && mapRef.current) {
      mapRef.current.removeLayer(lkpMarkerRef.current);
      lkpMarkerRef.current = null;
    }
    setFormData(prev => ({
      ...prev,
      Latitude: '',
      Longitude: '',
      Morada_Exacta_Coordenadas: ''
    }));
  };

  // Remove Residence marker
  const handleRemoveResidence = () => {
    if (residenceMarkerRef.current && mapRef.current) {
      mapRef.current.removeLayer(residenceMarkerRef.current);
      residenceMarkerRef.current = null;
    }
    setFormData(prev => ({
      ...prev,
      Residencia_Latitude: '',
      Residencia_Longitude: '',
      Residencia_Morada: ''
    }));
  };

  // Remove Relevant point
  const handleRemoveRelevantPoint = (pointId) => {
    setRelevantPoints(prev => {
      const updated = prev.filter(p => p.id !== pointId);
      setFormData(f => ({
        ...f,
        Locais_Relevantes: updated,
        Locais_Relevantes_GeoJSON: JSON.stringify(updated)
      }));
      syncRelevantMarkers(updated);
      return updated;
    });
  };

  // Form validation according to PDGNR M 1-04-02 minimum criteria
  const validateForm = () => {
    const errors = {};
    if (!formData.Denunciante_Nome || String(formData.Denunciante_Nome).trim() === '') {
      errors.Denunciante_Nome = 'Campo obrigatório';
    }
    if (!formData.Denunciante_Contacto && !formData.Denunciante_Telefone) {
      errors.Denunciante_Contacto = 'Contacto obrigatório';
    }
    if (!formData.Nome_Completo || String(formData.Nome_Completo).trim() === '') {
      errors.Nome_Completo = 'Nome da vítima obrigatório';
    }
    if (!formData.Sexo || String(formData.Sexo).trim() === '') {
      errors.Sexo = 'Sexo obrigatório';
    }
    if (!formData.Idade_Exacta && !formData.Data_Nascimento) {
      errors.Idade_Exacta = 'Indique a idade ou data de nascimento';
    }
    if (!formData.Data_Desaparecimento || String(formData.Data_Desaparecimento).trim() === '') {
      errors.Data_Desaparecimento = 'Data obrigatória';
    }
    if (!formData.Hora_Desaparecimento || String(formData.Hora_Desaparecimento).trim() === '') {
      errors.Hora_Desaparecimento = 'Hora obrigatória';
    }
    if (!formData.Local_Ultimo_Avistamento || String(formData.Local_Ultimo_Avistamento).trim() === '') {
      errors.Local_Ultimo_Avistamento = 'Local do último avistamento obrigatório';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Discard draft with institutional confirmation modal
  const handleDiscardDraft = async () => {
    const confirmed = await showModal(
      'Descartar Rascunho',
      'Tem a certeza de que deseja descartar este rascunho? Todos os dados temporários preenchidos serão permanentemente eliminados.',
      { confirmText: 'Sim, Descartar', cancelText: 'Voltar', isDanger: true }
    );
    if (confirmed) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
      if (lkpMarkerRef.current && mapRef.current) {
        mapRef.current.removeLayer(lkpMarkerRef.current);
        lkpMarkerRef.current = null;
      }
      if (residenceMarkerRef.current && mapRef.current) {
        mapRef.current.removeLayer(residenceMarkerRef.current);
        residenceMarkerRef.current = null;
      }
      relevantMarkersRef.current.forEach(m => {
        try { mapRef.current.removeLayer(m); } catch (_) {}
      });
      relevantMarkersRef.current = [];
      setRelevantPoints([]);
      setFormData({});
      setValidationErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Submit official form to backend
  const submitForm = async () => {
    if (!validateForm()) {
      await showModal('Validação de Dados', 'Por favor, preencha os campos obrigatórios assinalados a vermelho.', { confirmText: 'OK' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setProcessing(true);
    try {
      const payload = { ...formData };
      payload.Data_Registo = new Date().toISOString().split('T')[0];
      payload.Hora_Registo = new Date().toTimeString().split(' ')[0].substring(0, 5);
      payload.Locais_Relevantes = relevantPoints;
      payload.Locais_Relevantes_GeoJSON = JSON.stringify(relevantPoints);

      const resp = await apiFetch('/api/casos-oficial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await resp.json();

      if (resp.ok) {
        try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
        const msg = `O caso de ${payload.Nome_Completo || 'Desconhecido'} foi registado com sucesso (ID: ${result.id || result.ID_Caso || 'N/A'}).`;
        await showModal('Caso Registado', msg, { confirmText: 'OK' });
        const notifySuccess = onCaseRegistered || onCaseSubmitted;
        if (notifySuccess) notifySuccess();
      } else {
        await showModal('Erro de Registo', `Falha ao registar o caso: ${result.error || 'Erro desconhecido no servidor'}`, { confirmText: 'OK' });
      }
    } catch (error) {
      console.error(error);
      await showModal('Erro de Comunicação', 'Ocorreu um erro de comunicação com o servidor central.', { confirmText: 'OK' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="registration-container">
      {/* Sticky Fast Navigation Sidebar */}
      <aside className="registration-sidebar">
        <h3>Menu Rápido</h3>
        <ul>
          {SECTIONS_LIST.map(sec => (
            <li key={sec.id}>
              <button
                type="button"
                className={activeSection === sec.id ? 'active' : ''}
                onClick={() => scrollToSection(sec.id)}
              >
                {sec.title}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Main Registration Content - 10 Blocks */}
      <main className="registration-main">

        {/* BLOCO 1: Denunciante & Autoridade GNR */}
        <section id="sec-denunciante" className="registration-section">
          <h2>1. Denunciante & Autoridade</h2>
          <p className="subtitle">Identificação detalhada de quem reporta o desaparecimento e do elemento militar que efetua o registo.</p>

          <div className="form-grid">
            <div className="form-group">
              <label>Nome do Denunciante *</label>
              <input
                type="text"
                value={formData.Denunciante_Nome || ''}
                onChange={e => handleChange('Denunciante_Nome', e.target.value)}
                placeholder="Nome completo do declarante"
              />
              {validationErrors.Denunciante_Nome && <span className="error-text">{validationErrors.Denunciante_Nome}</span>}
            </div>

            <div className="form-group">
              <label>Relação com o Desaparecido</label>
              <input
                type="text"
                placeholder="Ex: Pai, Mãe, Cônjuge, Vizinho, Tutor"
                value={formData.Denunciante_Relacao || ''}
                onChange={e => handleChange('Denunciante_Relacao', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Contacto Telefónico Principal *</label>
              <input
                type="tel"
                maxLength="9"
                placeholder="9 algarismos"
                value={formData.Denunciante_Contacto || ''}
                onChange={e => handleChange('Denunciante_Contacto', e.target.value)}
              />
              {validationErrors.Denunciante_Contacto && <span className="error-text">{validationErrors.Denunciante_Contacto}</span>}
            </div>

            <div className="form-group">
              <label>Contacto Alternativo</label>
              <input
                type="tel"
                maxLength="9"
                placeholder="9 algarismos"
                value={formData.Denunciante_Telefone_Alternativo || ''}
                onChange={e => handleChange('Denunciante_Telefone_Alternativo', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Morada do Denunciante</label>
              <input
                type="text"
                placeholder="Rua, número, localidade, código postal"
                value={formData.Denunciante_Morada || ''}
                onChange={e => handleChange('Denunciante_Morada', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>E-mail do Denunciante</label>
              <input
                type="email"
                placeholder="email@exemplo.pt"
                value={formData.Denunciante_Email || ''}
                onChange={e => handleChange('Denunciante_Email', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Disponibilidade de Contacto</label>
              <input
                type="text"
                placeholder="Ex: Disponível 24h, apenas pós-laboral"
                value={formData.Denunciante_Disponibilidade || ''}
                onChange={e => handleChange('Denunciante_Disponibilidade', e.target.value)}
              />
            </div>
          </div>

          <h4 style={{marginTop: '24px', marginBottom: '12px', color: '#1A365D', borderTop: '1px solid #E2E8F0', paddingTop: '16px'}}>
            Autoridade GNR / Militar Responsável
          </h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Nome do Elemento</label>
              <input
                type="text"
                value={formData.GNR_Nome_Elemento || ''}
                onChange={e => handleChange('GNR_Nome_Elemento', e.target.value)}
                placeholder="Nome do operador"
              />
            </div>
            <div className="form-group">
              <label>Posto / Graduação</label>
              <input
                type="text"
                value={formData.GNR_Posto_Elemento || ''}
                onChange={e => handleChange('GNR_Posto_Elemento', e.target.value)}
                placeholder="Ex: Guarda, Cabo, Sargento..."
              />
            </div>
            <div className="form-group">
              <label>Unidade / Destacamento</label>
              <input
                type="text"
                value={formData.GNR_Unidade_Elemento || ''}
                onChange={e => handleChange('GNR_Unidade_Elemento', e.target.value)}
                placeholder="Ex: Posto Territorial de Sintra"
              />
            </div>
            <div className="form-group">
              <label>NIM (7 dígitos)</label>
              <input
                type="text"
                maxLength={7}
                value={formData.GNR_NIM_Elemento || ''}
                onChange={e => handleChange('GNR_NIM_Elemento', e.target.value)}
                placeholder="Ex: 2150123"
              />
            </div>
          </div>
        </section>

        {/* BLOCO 2: Identificação Pessoal do Desaparecido */}
        <section id="sec-vitima" className="registration-section">
          <h2>2. Identificação da Vítima</h2>
          <p className="subtitle">Dados biográficos e estado civil da pessoa desaparecida.</p>

          <div className="form-grid">
            <div className="form-group full-width">
              <label>Nome Completo da Vítima *</label>
              <input
                type="text"
                value={formData.Nome_Completo || ''}
                onChange={e => handleChange('Nome_Completo', e.target.value)}
                placeholder="Nome completo conforme documento de identificação"
              />
              {validationErrors.Nome_Completo && <span className="error-text">{validationErrors.Nome_Completo}</span>}
            </div>

            <div className="form-group">
              <label>Alcunha / Nome Social</label>
              <input
                type="text"
                placeholder="Nome pelo qual é conhecido na comunidade"
                value={formData.Alcunha || ''}
                onChange={e => handleChange('Alcunha', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Data de Nascimento</label>
              <input
                type="date"
                value={formData.Data_Nascimento || ''}
                onChange={e => handleChange('Data_Nascimento', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Idade (Anos) * <InfoHelper text="Calculada automaticamente a partir da data de nascimento se preenchida." /></label>
              <input
                type="number"
                min="0"
                max="125"
                value={formData.Idade_Exacta || ''}
                onChange={e => handleChange('Idade_Exacta', e.target.value)}
              />
              {validationErrors.Idade_Exacta && <span className="error-text">{validationErrors.Idade_Exacta}</span>}
            </div>

            <div className="form-group">
              <label>Sexo *</label>
              <select value={formData.Sexo || ''} onChange={e => handleChange('Sexo', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="Masculino">Masculino</option>
                <option value="Feminino">Feminino</option>
                <option value="Outro">Outro / Não Determinado</option>
              </select>
              {validationErrors.Sexo && <span className="error-text">{validationErrors.Sexo}</span>}
            </div>

            <div className="form-group">
              <label>Nacionalidade</label>
              <input
                type="text"
                placeholder="Ex: Portuguesa"
                value={formData.Nacionalidade || 'Portuguesa'}
                onChange={e => handleChange('Nacionalidade', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Estado Civil</label>
              <select value={formData.Estado_Civil || ''} onChange={e => handleChange('Estado_Civil', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="Solteiro">Solteiro/a</option>
                <option value="Casado">Casado/a</option>
                <option value="União de Facto">União de Facto</option>
                <option value="Divorciado">Divorciado/a</option>
                <option value="Separado">Separado/a Judicialmente</option>
                <option value="Viúvo">Viúvo/a</option>
              </select>
            </div>

            <div className="form-group">
              <label>Nome do Pai</label>
              <input
                type="text"
                placeholder="Filiação paterna"
                value={formData.Filiacao_Pai || ''}
                onChange={e => handleChange('Filiacao_Pai', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Nome da Mãe</label>
              <input
                type="text"
                placeholder="Filiação materna"
                value={formData.Filiacao_Mae || ''}
                onChange={e => handleChange('Filiacao_Mae', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Número de Filhos</label>
              <input
                type="number"
                min="0"
                placeholder="0"
                value={formData.Numero_Filhos || ''}
                onChange={e => handleChange('Numero_Filhos', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Línguas Faladas</label>
              <input
                type="text"
                placeholder="Ex: Português, Inglês, Francês"
                value={formData.Linguas_Faladas || ''}
                onChange={e => handleChange('Linguas_Faladas', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* BLOCO 3: Características Físicas e Sinais Distintivos */}
        <section id="sec-fisicas" className="registration-section">
          <h2>3. Características & Sinais</h2>
          <p className="subtitle">Descrição morfológica e elementos distintivos para reconhecimento no terreno.</p>

          <div className="form-grid">
            <div className="form-group">
              <label>Altura Estimada (cm)</label>
              <input
                type="number"
                placeholder="Ex: 175"
                value={formData.Altura_Estimada || ''}
                onChange={e => handleChange('Altura_Estimada', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Peso Aproximado (kg)</label>
              <input
                type="number"
                placeholder="Ex: 72"
                value={formData.Peso_Aproximado || ''}
                onChange={e => handleChange('Peso_Aproximado', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Compleição Física</label>
              <select value={formData.Compleicao_Fisica || ''} onChange={e => handleChange('Compleicao_Fisica', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="Magro">Magro/a</option>
                <option value="Normal">Normal / Média</option>
                <option value="Robusto">Robusto/a / Forte</option>
                <option value="Obeso">Obeso/a</option>
                <option value="Franzino">Franzino/a / Débil</option>
              </select>
            </div>

            <div className="form-group">
              <label>Cor do Cabelo</label>
              <select value={formData.Cor_Cabelo || ''} onChange={e => handleChange('Cor_Cabelo', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="Castanho">Castanho</option>
                <option value="Preto">Preto</option>
                <option value="Louro">Louro</option>
                <option value="Grisalho">Grisalho</option>
                <option value="Branco">Branco</option>
                <option value="Ruivo">Ruivo</option>
                <option value="Careca">Careca / Calvo</option>
              </select>
            </div>

            <div className="form-group">
              <label>Tipo de Cabelo</label>
              <select value={formData.Tipo_Cabelo || ''} onChange={e => handleChange('Tipo_Cabelo', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="Curto">Curto</option>
                <option value="Comprido">Comprido</option>
                <option value="Médio">Médio</option>
                <option value="Liso">Liso</option>
                <option value="Ondulado">Ondulado</option>
                <option value="Encaracolado">Encaracolado</option>
              </select>
            </div>

            <div className="form-group">
              <label>Cor dos Olhos</label>
              <select value={formData.Cor_Olhos || ''} onChange={e => handleChange('Cor_Olhos', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="Castanhos">Castanhos</option>
                <option value="Azuis">Azuis</option>
                <option value="Verdes">Verdes</option>
                <option value="Negros">Negros</option>
                <option value="Cinzentos">Cinzentos</option>
              </select>
            </div>

            <div className="form-group">
              <label>Tom de Pele</label>
              <select value={formData.Tom_Pele || ''} onChange={e => handleChange('Tom_Pele', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="Clara">Clara</option>
                <option value="Morena">Morena</option>
                <option value="Negra">Negra</option>
                <option value="Pálida">Pálida</option>
              </select>
            </div>

            <div className="form-group">
              <label>Barba / Bigode</label>
              <select value={formData.Barba_Bigode || ''} onChange={e => handleChange('Barba_Bigode', e.target.value)}>
                <option value="">Selecione...</option>
                <option value="Sem barba">Sem barba / Rapado</option>
                <option value="Barba curta">Barba curta</option>
                <option value="Barba cerrada">Barba cerrada</option>
                <option value="Bigode">Bigode</option>
                <option value="Pêra">Pêra</option>
              </select>
            </div>

            <div className="form-group">
              <label>Tamanho do Calçado</label>
              <input
                type="number"
                placeholder="Ex: 42"
                value={formData.Tamanho_Calcado || ''}
                onChange={e => handleChange('Tamanho_Calcado', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Sinais Distintivos Gerais <InfoHelper text="Marcas, deformações físicas, óculos graduados, aparelhos auditivos ou próteses dentárias." /></label>
              <input
                type="text"
                placeholder="Ex: Usa óculos com armação preta, falta de dentes incisivos superiores..."
                value={formData.Sinais_Distintivos || ''}
                onChange={e => handleChange('Sinais_Distintivos', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Cicatrizes e Marcas Operatórias</label>
              <input
                type="text"
                placeholder="Ex: Cicatriz cirúrgica de apêndice no abdómen, cicatriz na têmpora esquerda..."
                value={formData.Cicatrizes_Marcas || ''}
                onChange={e => handleChange('Cicatrizes_Marcas', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Tatuagens (Descrição e Localização)</label>
              <input
                type="text"
                placeholder="Ex: Andorinha no antebraço direito, nome 'Maria' no peito..."
                value={formData.Tatuagens_Descricao || ''}
                onChange={e => handleChange('Tatuagens_Descricao', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Fotografia Recente Disponível?</label>
              <select
                value={formData.Fotografia_Recente_Disponivel || ''}
                onChange={e => handleChange('Fotografia_Recente_Disponivel', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Sim">Sim - Fornecida pela família</option>
                <option value="Não">Não - Pendente de obtenção</option>
              </select>
            </div>
          </div>
        </section>

        {/* BLOCO 4: Vestuário por Camadas e Cinotecnia K9 */}
        <section id="sec-vestuario" className="registration-section">
          <h2>4. Vestuário & Cinotecnia K9</h2>
          <p className="subtitle">Descrição das peças de vestuário e identificação de artigos de odor não lavados para binómios cinotécnicos de busca.</p>

          <div className="form-grid">
            <div className="form-group full-width">
              <label>Roupa Superior</label>
              <input
                type="text"
                placeholder="Ex: T-shirt branca com estampado, camisa axadrezada azul e verde..."
                value={formData.Roupa_Superior || ''}
                onChange={e => handleChange('Roupa_Superior', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Roupa Inferior</label>
              <input
                type="text"
                placeholder="Ex: Calças de ganga azul escura, calções desportivos pretos..."
                value={formData.Roupa_Inferior || ''}
                onChange={e => handleChange('Roupa_Inferior', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Agasalho Exterior</label>
              <input
                type="text"
                placeholder="Ex: Blusão polar vermelho com capuz, impermeável preto..."
                value={formData.Agasalho_Exterior || ''}
                onChange={e => handleChange('Agasalho_Exterior', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Tipo de Calçado e Cor</label>
              <input
                type="text"
                placeholder="Ex: Botas de montanha castanhas, ténis pretos..."
                value={formData.Tipo_Calcado_Cor || ''}
                onChange={e => handleChange('Tipo_Calcado_Cor', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Tipo de Sola / Rasto <InfoHelper text="Informação crucial para equipas de rastreadores apeados e identificação de pegadas." /></label>
              <input
                type="text"
                placeholder="Ex: Piso tratorado com rasto em cruz, sola lisa de couro..."
                value={formData.Tipo_Sola_Rasto || ''}
                onChange={e => handleChange('Tipo_Sola_Rasto', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Acessórios, Joias e Outros Objetos Visíveis</label>
              <input
                type="text"
                placeholder="Ex: Mochila preta da marca Quechua, relógio prateado no pulso esquerdo, boné azul..."
                value={formData.Acessorios_Oculos_Joias || ''}
                onChange={e => handleChange('Acessorios_Oculos_Joias', e.target.value)}
              />
            </div>
          </div>

          {/* Doutrina SAR de Cinotecnia K9 */}
          <div className="k9-odor-box">
            <div className="k9-odor-title">Doutrina SAR: Preservação de Artigo de Odor para Cães de Rasto (K9)</div>
            <div className="k9-odor-desc">
              Para o acionamento eficaz de binómios cinotécnicos de mantrailing/rasto, é imprescindível identificar uma peça de roupa não lavada (ex: fronha de almofada, camisola interior usada recentemente). O artigo NÃO deve ser manipulado desnecessariamente e deve ser isolado num saco limpo.
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Artigo de Odor K9 Disponível?</label>
                <select
                  value={formData.K9_Artigo_Odor_Disponivel || ''}
                  onChange={e => handleChange('K9_Artigo_Odor_Disponivel', e.target.value)}
                >
                  <option value="">Selecione...</option>
                  <option value="Sim">Sim - Identificado e preservado</option>
                  <option value="Não">Não disponível</option>
                  <option value="Em averiguação">Em averiguação com a família</option>
                </select>
              </div>

              <div className="form-group">
                <label>Descrição do Artigo de Odor</label>
                <input
                  type="text"
                  placeholder="Ex: Fronha de almofada usada na noite anterior, camisola de dormir"
                  value={formData.K9_Descricao_Artigo_Odor || ''}
                  onChange={e => handleChange('K9_Descricao_Artigo_Odor', e.target.value)}
                />
              </div>

              <div className="form-group full-width">
                <label>Localização e Condições de Guarda do Artigo</label>
                <input
                  type="text"
                  placeholder="Ex: Quarto da vítima, fechado no roupeiro, guardado em saco plástico limpo pelo denunciante"
                  value={formData.K9_Localizacao_Artigo_Odor || ''}
                  onChange={e => handleChange('K9_Localizacao_Artigo_Odor', e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* BLOCO 5: Circunstâncias e Cartografia Multi-Pontos */}
        <section id="sec-mapa" className="registration-section">
          <h2>5. Cartografia Multi-Pontos</h2>
          <p className="subtitle">
            Registo georreferenciado do Local do Desaparecimento (LKP), Residência habitual da vítima e Outros Locais Relevantes para modelação do perímetro de busca e aprendizagem preditiva.
          </p>

          <div className="form-grid">
            <div className="form-group">
              <label>Data do Desaparecimento *</label>
              <input
                type="date"
                max={new Date().toISOString().split('T')[0]}
                value={formData.Data_Desaparecimento || ''}
                onChange={e => handleChange('Data_Desaparecimento', e.target.value)}
              />
              {validationErrors.Data_Desaparecimento && <span className="error-text">{validationErrors.Data_Desaparecimento}</span>}
            </div>

            <div className="form-group">
              <label>Hora do Desaparecimento *</label>
              <input
                type="time"
                value={formData.Hora_Desaparecimento || ''}
                onChange={e => handleChange('Hora_Desaparecimento', e.target.value)}
              />
              {validationErrors.Hora_Desaparecimento && <span className="error-text">{validationErrors.Hora_Desaparecimento}</span>}
            </div>

            <div className="form-group full-width">
              <label>Local do Último Avistamento (LKP / PLS) * <InfoHelper text="Designação toponímica ou descrição textual do local exato onde a pessoa foi vista pela última vez." /></label>
              <input
                type="text"
                placeholder="Ex: Cruzamento da Rua Principal com o Caminho dos Pinhais, junto ao café Central"
                value={formData.Local_Ultimo_Avistamento || ''}
                onChange={e => handleChange('Local_Ultimo_Avistamento', e.target.value)}
              />
              {validationErrors.Local_Ultimo_Avistamento && <span className="error-text">{validationErrors.Local_Ultimo_Avistamento}</span>}
            </div>

            <div className="form-group">
              <label>Freguesia</label>
              <input
                type="text"
                value={formData.Freguesia || ''}
                onChange={e => handleChange('Freguesia', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Concelho</label>
              <input
                type="text"
                value={formData.Concelho || ''}
                onChange={e => handleChange('Concelho', e.target.value)}
              />
            </div>
          </div>

          {/* Map Point Mode Selector & Geocoding Toolbar */}
          <div style={{marginTop: '24px'}}>
            <label style={{fontSize: '14px', fontWeight: '600', color: '#1A365D', display: 'block', marginBottom: '8px'}}>
              Módulo Cartográfico Operacional
            </label>

            {/* Point Selection Toolbar */}
            <div className="map-point-modes">
              <span className="map-point-modes-title">Modo de Ponto no Mapa:</span>
              <button
                type="button"
                className={`point-mode-btn mode-lkp ${mapMarkerMode === 'lkp' ? 'active' : ''}`}
                onClick={() => setMapMarkerMode('lkp')}
              >
                Local Desaparecimento (LKP) *
              </button>

              <button
                type="button"
                className={`point-mode-btn mode-residence ${mapMarkerMode === 'residence' ? 'active' : ''}`}
                onClick={() => setMapMarkerMode('residence')}
              >
                Residência da Vítima
              </button>

              <button
                type="button"
                className={`point-mode-btn mode-relevant ${mapMarkerMode === 'relevant' ? 'active' : ''}`}
                onClick={() => setMapMarkerMode('relevant')}
              >
                Outro Local Relevante
              </button>

              {mapMarkerMode === 'relevant' && (
                <div className="relevant-point-input-row">
                  <input
                    type="text"
                    placeholder="Nome do local (ex: Café habitual, Trabalho, Estação)"
                    value={relevantPointLabel}
                    onChange={e => setRelevantPointLabel(e.target.value)}
                  />
                  <select
                    value={relevantPointType}
                    onChange={e => setRelevantPointType(e.target.value)}
                  >
                    <option value="Local Frequente">Local Frequente</option>
                    <option value="Local de Trabalho">Local de Trabalho</option>
                    <option value="Escola / Centro">Escola / Centro</option>
                    <option value="Viatura Abandonada">Viatura Abandonada</option>
                    <option value="Apeadeiro / Estação">Apeadeiro / Estação</option>
                    <option value="Cemitério / Afetivo">Cemitério / Afetivo</option>
                    <option value="Outro">Outro Ponto de Interesse</option>
                  </select>
                </div>
              )}
            </div>

            {/* Search and Layer controls */}
            <div className="map-toolbar-container">
              <div className="map-toolbar-row">
                <MapSearchBar
                  map={mapRef.current}
                  onSelectLocation={handleLocationSelected}
                  placeholder="Pesquisar morada, localidade ou coordenadas GPS (ex: 39.91, -8.62)..."
                />
                <MapLayerSwitcher
                  activeLayer={activeMapLayer}
                  onLayerChange={handleBaseLayerChange}
                />
              </div>
            </div>

            {/* Leaflet container */}
            <div id="mappicker" className="map-container-registration"></div>

            {/* Registered Points Panel */}
            <div className="registered-points-panel">
              <div className="registered-points-title">Pontos Georreferenciados Registados</div>
              <div className="registered-points-list">
                {/* LKP Point */}
                <div className="registered-point-item">
                  <div className="registered-point-details">
                    <span className="point-badge badge-lkp">LKP</span>
                    {formData.Latitude && formData.Longitude ? (
                      <>
                        <span className="registered-point-coords">{parseFloat(formData.Latitude).toFixed(5)}, {parseFloat(formData.Longitude).toFixed(5)}</span>
                        <span className="registered-point-address">{formData.Local_Ultimo_Avistamento || formData.Morada_Exacta_Coordenadas}</span>
                      </>
                    ) : (
                      <span style={{color: '#E53E3E', fontStyle: 'italic'}}>Ainda não assinalado no mapa (clique no mapa no modo LKP)</span>
                    )}
                  </div>
                  {formData.Latitude && formData.Longitude && (
                    <div className="registered-point-actions">
                      <button type="button" className="btn-point-action" onClick={() => handleRecenter(formData.Latitude, formData.Longitude)}>
                        Recentrar
                      </button>
                      <button type="button" className="btn-point-action btn-remove" onClick={handleRemoveLkp}>
                        Limpar
                      </button>
                    </div>
                  )}
                </div>

                {/* Residence Point */}
                <div className="registered-point-item">
                  <div className="registered-point-details">
                    <span className="point-badge badge-residence">Residência</span>
                    {formData.Residencia_Latitude && formData.Residencia_Longitude ? (
                      <>
                        <span className="registered-point-coords">{parseFloat(formData.Residencia_Latitude).toFixed(5)}, {parseFloat(formData.Residencia_Longitude).toFixed(5)}</span>
                        <span className="registered-point-address">{formData.Residencia_Morada || 'Residência habitual registada'}</span>
                      </>
                    ) : (
                      <span style={{color: '#718096', fontStyle: 'italic'}}>Opcional: selecione o modo Residência e marque no mapa</span>
                    )}
                  </div>
                  {formData.Residencia_Latitude && formData.Residencia_Longitude && (
                    <div className="registered-point-actions">
                      <button type="button" className="btn-point-action" onClick={() => handleRecenter(formData.Residencia_Latitude, formData.Residencia_Longitude)}>
                        Recentrar
                      </button>
                      <button type="button" className="btn-point-action btn-remove" onClick={handleRemoveResidence}>
                        Limpar
                      </button>
                    </div>
                  )}
                </div>

                {/* Other Relevant Points */}
                {relevantPoints.map(pt => (
                  <div key={pt.id} className="registered-point-item">
                    <div className="registered-point-details">
                      <span className="point-badge badge-relevant">{pt.tipo || 'Ponto'}</span>
                      <span className="registered-point-coords">{parseFloat(pt.latitude).toFixed(5)}, {parseFloat(pt.longitude).toFixed(5)}</span>
                      <span className="registered-point-address"><strong>{pt.label}:</strong> {pt.address || 'Sem morada'}</span>
                    </div>
                    <div className="registered-point-actions">
                      <button type="button" className="btn-point-action" onClick={() => handleRecenter(pt.latitude, pt.longitude)}>
                        Recentrar
                      </button>
                      <button type="button" className="btn-point-action btn-remove" onClick={() => handleRemoveRelevantPoint(pt.id)}>
                        Remover
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="form-grid" style={{marginTop: '20px'}}>
            <div className="form-group">
              <label>Condições Meteorológicas no Momento</label>
              <select
                value={formData.Condicoes_Meteorologicas || ''}
                onChange={e => handleChange('Condicoes_Meteorologicas', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Bom tempo / Céu limpo">Bom tempo / Céu limpo</option>
                <option value="Chuva fraca / Chuvisco">Chuva fraca / Chuvisco</option>
                <option value="Chuva intensa / Tempestade">Chuva intensa / Tempestade</option>
                <option value="Nevoeiro cerrado">Nevoeiro cerrado / Visibilidade reduzida</option>
                <option value="Frio extremo / Geada">Frio extremo / Geada / Risco de hipotermia</option>
                <option value="Calor extremo">Calor extremo / Risco de desidratação</option>
                <option value="Vento forte">Vento forte</option>
              </select>
            </div>

            <div className="form-group">
              <label>Tipo de Terreno Envolvente</label>
              <select
                value={formData.Tipo_Terreno_Envolvente || ''}
                onChange={e => handleChange('Tipo_Terreno_Envolvente', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Urbano denso">Urbano denso</option>
                <option value="Suburbano / Residencial">Suburbano / Residencial</option>
                <option value="Rural / Agrícola">Rural / Agrícola</option>
                <option value="Florestal / Montanha">Florestal / Montanha / Declives</option>
                <option value="Costeiro / Falésias">Costeiro / Falésias / Praias</option>
                <option value="Ribeirinho / Linhas de água">Ribeirinho / Proximidade de rios ou barragens</option>
                <option value="Misto">Misto (Urbano e Mata)</option>
              </select>
            </div>
          </div>
        </section>

        {/* BLOCO 6: Contactos, Meios Digitais e Veículo */}
        <section id="sec-comunicacoes" className="registration-section">
          <h2>6. Meios & Viatura</h2>
          <p className="subtitle">Comunicações móveis, identificadores eletrónicos e viatura envolvida.</p>

          <div className="form-grid">
            <div className="form-group">
              <label>Telemóvel da Vítima</label>
              <input
                type="tel"
                maxLength="9"
                placeholder="9 algarismos"
                value={formData.Telemovel_Principal || formData.Contacto_Telemovel || ''}
                onChange={e => {
                  handleChange('Telemovel_Principal', e.target.value);
                  handleChange('Contacto_Telemovel', e.target.value);
                }}
              />
            </div>

            <div className="form-group">
              <label>Operador de Rede Móvel</label>
              <select
                value={formData.Operador_Telemovel || ''}
                onChange={e => handleChange('Operador_Telemovel', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="MEO">MEO</option>
                <option value="NOS">NOS</option>
                <option value="Vodafone">Vodafone</option>
                <option value="NOWO">NOWO</option>
                <option value="Outro">Outro / Estrangeiro</option>
              </select>
            </div>

            <div className="form-group">
              <label>Levava o Telemóvel Consigo?</label>
              <select
                value={formData.Telemovel_Consigo || ''}
                onChange={e => handleChange('Telemovel_Consigo', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Sim">Sim - Levou consigo</option>
                <option value="Não">Não - Ficou em casa/trabalho</option>
                <option value="Desconhecido">Desconhecido</option>
              </select>
            </div>

            <div className="form-group">
              <label>Estado do Telemóvel (Ao Chamar)</label>
              <select
                value={formData.Telemovel_Chama_Ou_Desligado || ''}
                onChange={e => handleChange('Telemovel_Chama_Ou_Desligado', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Chama normalmente">Chama normalmente (com sinal de toque)</option>
                <option value="Desligado / Sem rede">Desligado / Sem cobertura de rede</option>
                <option value="Voicemail imediato">Reencaminha logo para correio de voz</option>
                <option value="Não atende">Chama até cair</option>
                <option value="Não tentado">Ainda não foi tentado contacto</option>
              </select>
            </div>

            <div className="form-group">
              <label>Viatura Envolvida: Marca e Modelo</label>
              <input
                type="text"
                placeholder="Ex: Renault Clio, Opel Corsa"
                value={formData.Veiculo_Marca_Modelo || formData.Veiculo_Envolvido || ''}
                onChange={e => {
                  handleChange('Veiculo_Marca_Modelo', e.target.value);
                  handleChange('Veiculo_Envolvido', e.target.value);
                }}
              />
            </div>

            <div className="form-group">
              <label>Cor da Viatura</label>
              <input
                type="text"
                placeholder="Ex: Cinzento escuro, Branco"
                value={formData.Veiculo_Cor || ''}
                onChange={e => handleChange('Veiculo_Cor', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Matrícula</label>
              <input
                type="text"
                placeholder="Ex: 00-AA-00"
                value={formData.Veiculo_Matricula || ''}
                onChange={e => handleChange('Veiculo_Matricula', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Possui Identificador Via Verde?</label>
              <select
                value={formData.Veiculo_Via_Verde || ''}
                onChange={e => handleChange('Veiculo_Via_Verde', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Sim">Sim - Ativo</option>
                <option value="Não">Não possui</option>
                <option value="Desconhecido">Desconhecido</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Transportes Públicos Habituais</label>
              <input
                type="text"
                placeholder="Ex: Utiliza habitualmente a linha de Sintra (CP) ou autocarro Carris Metropolitana nº 1234"
                value={formData.Transportes_Publicos_Habituais || ''}
                onChange={e => handleChange('Transportes_Publicos_Habituais', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* BLOCO 7: Estado de Saúde, Autonomia e Medicação */}
        <section id="sec-saude" className="registration-section">
          <h2>7. Saúde, Autonomia & Medicação</h2>
          <p className="subtitle">Condições neurodegenerativas, patologias mentais, limitações físicas e dependência de medicação vital.</p>

          <div className="form-grid">
            <div className="form-group">
              <label>Diagnóstico de Alzheimer / Demência?</label>
              <select
                value={formData.Diagnostico_Demencia_Alzheimer || ''}
                onChange={e => handleChange('Diagnostico_Demencia_Alzheimer', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Sim">Sim - Confirmado clinicamente</option>
                <option value="Não">Não</option>
                <option value="Suspeita">Suspeita clínica em avaliação</option>
              </select>
            </div>

            <div className="form-group">
              <label>Doença Psiquiátrica / Depressão?</label>
              <select
                value={formData.Doenca_Psiquiatrica_Depressao || ''}
                onChange={e => handleChange('Doenca_Psiquiatrica_Depressao', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Sim">Sim - Diagnosticada</option>
                <option value="Não">Não</option>
                <option value="Episódio Depressivo">Episódio depressivo agudo recente</option>
              </select>
            </div>

            <div className="form-group">
              <label>Falta de Autonomia Locomotora / Mobilidade?</label>
              <select
                value={formData.Falta_Autonomia_Locomotora || ''}
                onChange={e => handleChange('Falta_Autonomia_Locomotora', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Total">Total - Acamado / Cadeira de rodas</option>
                <option value="Parcial">Parcial - Apoio de bengala / Marcha lenta</option>
                <option value="Autónomo">Autónomo - Mobilidade normal</option>
              </select>
            </div>

            <div className="form-group">
              <label>Depende de Medicação Vital?</label>
              <select
                value={formData.Medicacao_Vital || ''}
                onChange={e => handleChange('Medicacao_Vital', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Sim">Sim - Interrupção causa risco de vida</option>
                <option value="Não">Não</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Descrição da Medicação Vital e Consequências da Falta</label>
              <input
                type="text"
                placeholder="Ex: Insulina para diabetes tipo 1, anticoagulantes, medicação cardíaca, antipsicóticos..."
                value={formData.Medicacao_Vital_Descricao || formData.Doencas_Cronicas || ''}
                onChange={e => {
                  handleChange('Medicacao_Vital_Descricao', e.target.value);
                  handleChange('Doencas_Cronicas', e.target.value);
                }}
              />
            </div>

            <div className="form-group">
              <label>Levou a Medicação Consigo?</label>
              <select
                value={formData.Medicacao_Levada_Consigo || ''}
                onChange={e => handleChange('Medicacao_Levada_Consigo', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Não">Não - Ficou em casa (Alerta de risco)</option>
                <option value="Sim">Sim - Levou consigo</option>
                <option value="Desconhecido">Desconhecido</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Quadro Clínico Geral e Estado Mental <InfoHelper text="Descreva alterações comportamentais recentes, episódios prévios de desorientação temporal/espacial." /></label>
              <textarea
                placeholder="Registe livremente detalhes sobre saúde física e psicológica..."
                value={formData.Estado_Mental || formData.Condicao_Fisica || ''}
                onChange={e => {
                  handleChange('Estado_Mental', e.target.value);
                  handleChange('Condicao_Fisica', e.target.value);
                }}
                rows={3}
              />
            </div>
          </div>
        </section>

        {/* BLOCO 8: Matriz Oficial de Risco PDGNR (15 Indicadores Oficiais) */}
        <section id="sec-risco" className="registration-section">
          <h2>8. Matriz de Risco (15 Indicadores)</h2>
          <p className="subtitle">
            Grelha oficial de critérios de risco elevado estipulados no Manual PDGNR M 1-04-02. O acionamento de qualquer indicador eleva a prioridade operacional e mobiliza meios imediatos.
          </p>

          <div className="risk-matrix-grid">
            {/* 1. Menor de Idade */}
            <div className={`risk-card ${formData.Pessoa_Menor_Idade === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Pessoa_Menor_Idade === 'Sim'}
                  onChange={e => handleCheckboxGroup('Pessoa_Menor_Idade', e.target.checked)}
                />
                <div>
                  <strong>1. Menor de Idade (&lt; 18 anos)</strong>
                  <span className="risk-card-desc">Criança ou adolescente sem plena autonomia.</span>
                </div>
              </label>
            </div>

            {/* 2. Idoso Vulnerável */}
            <div className={`risk-card ${formData.Pessoa_Idosa === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Pessoa_Idosa === 'Sim'}
                  onChange={e => handleCheckboxGroup('Pessoa_Idosa', e.target.checked)}
                />
                <div>
                  <strong>2. Idoso Vulnerável (&gt; 75 anos)</strong>
                  <span className="risk-card-desc">Pessoa de idade avançada com fragilidade física/cognitiva.</span>
                </div>
              </label>
            </div>

            {/* 3. Indícios de Crime */}
            <div className={`risk-card ${formData.Indicios_Crime === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Indicios_Crime === 'Sim'}
                  onChange={e => handleCheckboxGroup('Indicios_Crime', e.target.checked)}
                />
                <div>
                  <strong>3. Indícios de Ilícito Criminal</strong>
                  <span className="risk-card-desc">Suspeita de sequestro, rapto, extorsão ou violência.</span>
                </div>
              </label>
            </div>

            {/* 4. Risco Iminente de Vida */}
            <div className={`risk-card ${formData.Risco_Iminente_Vida === 'Sim' || formData.Verbalizou_Intencao_Suicidio === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Risco_Iminente_Vida === 'Sim' || formData.Verbalizou_Intencao_Suicidio === 'Sim'}
                  onChange={e => {
                    handleCheckboxGroup('Risco_Iminente_Vida', e.target.checked);
                    handleCheckboxGroup('Verbalizou_Intencao_Suicidio', e.target.checked);
                  }}
                />
                <div>
                  <strong>4. Risco Iminente para a Vida / Suicídio</strong>
                  <span className="risk-card-desc">Ideação suicida expressa, carta de despedida ou perigo letal.</span>
                </div>
              </label>
            </div>

            {/* 5. Risco Integridade Física */}
            <div className={`risk-card ${formData.Risco_Integridade_Fisica === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Risco_Integridade_Fisica === 'Sim'}
                  onChange={e => handleCheckboxGroup('Risco_Integridade_Fisica', e.target.checked)}
                />
                <div>
                  <strong>5. Risco para a Integridade Física</strong>
                  <span className="risk-card-desc">Condições meteorológicas adversas ou terreno inóspito.</span>
                </div>
              </label>
            </div>

            {/* 6. Ausência Contradiz Comportamento */}
            <div className={`risk-card ${formData.Ausencia_Contradiz_Comportamento === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Ausencia_Contradiz_Comportamento === 'Sim'}
                  onChange={e => handleCheckboxGroup('Ausencia_Contradiz_Comportamento', e.target.checked)}
                />
                <div>
                  <strong>6. Contradição com Comportamento Habitual</strong>
                  <span className="risk-card-desc">Ruptura súbita com a rotina e hábitos de comunicação.</span>
                </div>
              </label>
            </div>

            {/* 7. Ausência sem Explicação */}
            <div className={`risk-card ${formData.Ausencia_Sem_Explicacao === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Ausencia_Sem_Explicacao === 'Sim'}
                  onChange={e => handleCheckboxGroup('Ausencia_Sem_Explicacao', e.target.checked)}
                />
                <div>
                  <strong>7. Ausência sem Qualquer Explicação</strong>
                  <span className="risk-card-desc">Nenhum motivo razoável conhecido para a ausência.</span>
                </div>
              </label>
            </div>

            {/* 8. Não Chegou ao Destino */}
            <div className={`risk-card ${formData.Nao_Chegou_Destino === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Nao_Chegou_Destino === 'Sim'}
                  onChange={e => handleCheckboxGroup('Nao_Chegou_Destino', e.target.checked)}
                />
                <div>
                  <strong>8. Não Chegou ao Destino Previsto</strong>
                  <span className="risk-card-desc">Viagem planeada sem chegada confirmada no destino.</span>
                </div>
              </label>
            </div>

            {/* 9. Não Levou Pertences */}
            <div className={`risk-card ${formData.Nao_Levou_Pertences === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Nao_Levou_Pertences === 'Sim'}
                  onChange={e => handleCheckboxGroup('Nao_Levou_Pertences', e.target.checked)}
                />
                <div>
                  <strong>9. Deixou Pertences Pessoais Vitais</strong>
                  <span className="risk-card-desc">Carteira, identificação, dinheiro ou telemóvel deixados para trás.</span>
                </div>
              </label>
            </div>

            {/* 10. Abandonou Veículo */}
            <div className={`risk-card ${formData.Abandonou_Veiculo === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Abandonou_Veiculo === 'Sim'}
                  onChange={e => handleCheckboxGroup('Abandonou_Veiculo', e.target.checked)}
                />
                <div>
                  <strong>10. Abandonou Veículo na Via</strong>
                  <span className="risk-card-desc">Viatura localizada destrancada ou em local ermo/ponte.</span>
                </div>
              </label>
            </div>

            {/* 11. Perigo para Terceiros */}
            <div className={`risk-card ${formData.Perigo_Para_Terceiros === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Perigo_Para_Terceiros === 'Sim'}
                  onChange={e => handleCheckboxGroup('Perigo_Para_Terceiros', e.target.checked)}
                />
                <div>
                  <strong>11. Perigo Potencial para Terceiros</strong>
                  <span className="risk-card-desc">Histórico recente de agressividade descontrolada.</span>
                </div>
              </label>
            </div>

            {/* 12. Perturbações Mentais */}
            <div className={`risk-card ${formData.Possui_Perturbacoes_Mentais === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Possui_Perturbacoes_Mentais === 'Sim'}
                  onChange={e => handleCheckboxGroup('Possui_Perturbacoes_Mentais', e.target.checked)}
                />
                <div>
                  <strong>12. Perturbações Mentais / Demência</strong>
                  <span className="risk-card-desc">Doença neurológica grave ou surto psicótico ativo.</span>
                </div>
              </label>
            </div>

            {/* 13. Falta de Autonomia */}
            <div className={`risk-card ${formData.Falta_Autonomia === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Falta_Autonomia === 'Sim'}
                  onChange={e => handleCheckboxGroup('Falta_Autonomia', e.target.checked)}
                />
                <div>
                  <strong>13. Falta de Autonomia de Sobrevivência</strong>
                  <span className="risk-card-desc">Incapacidade de se alimentar, abrigar ou orientar sozinho.</span>
                </div>
              </label>
            </div>

            {/* 14. Falta Medicação Vital */}
            <div className={`risk-card ${formData.Medicacao_Vital === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Medicacao_Vital === 'Sim'}
                  onChange={e => handleCheckboxGroup('Medicacao_Vital', e.target.checked)}
                />
                <div>
                  <strong>14. Privação de Medicação Vital</strong>
                  <span className="risk-card-desc">Tratamento indispensável de toma diária interrompido.</span>
                </div>
              </label>
            </div>

            {/* 15. Violência Doméstica */}
            <div className={`risk-card ${formData.Vitima_Violencia_Domestica === 'Sim' ? 'active' : ''}`}>
              <label className="risk-card-label">
                <input
                  type="checkbox"
                  checked={formData.Vitima_Violencia_Domestica === 'Sim'}
                  onChange={e => handleCheckboxGroup('Vitima_Violencia_Domestica', e.target.checked)}
                />
                <div>
                  <strong>15. Vítima de Violência Doméstica</strong>
                  <span className="risk-card-desc">Histórico de agressões, queixas ativas ou ameaças de morte.</span>
                </div>
              </label>
            </div>
          </div>
        </section>

        {/* BLOCO 9: Circunstâncias Específicas (Bifurcação Condicional Menor vs Adulto) */}
        <section id="sec-condicional" className="registration-section">
          <h2>9. Circunstâncias Específicas</h2>
          <p className="subtitle">
            Campos especializados adaptados automaticamente à faixa etária da vítima: {isMinor ? 'Menor de Idade (< 18 anos)' : 'Adulto (>= 18 anos)'}.
          </p>

          {isMinor ? (
            /* Sub-bloco exclusivo para Menores de Idade */
            <div className="conditional-container minor">
              <div className="conditional-header minor">
                Registo de Menor de Idade (&lt; 18 anos) - Doutrina Tutelar PDGNR
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Fuga de Instituição / Centro Educativo / Lar?</label>
                  <select
                    value={formData.Menor_Fuga_Instituicao || formData.Fugiu_Centro_Educativo || ''}
                    onChange={e => {
                      handleChange('Menor_Fuga_Instituicao', e.target.value);
                      handleChange('Fugiu_Centro_Educativo', e.target.value);
                    }}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Fuga institucional</option>
                    <option value="Não">Não - Desaparecido de ambiente familiar</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Nome da Instituição (se aplicável)</label>
                  <input
                    type="text"
                    placeholder="Ex: Centro de Acolhimento Santa Maria"
                    value={formData.Menor_Nome_Instituicao || ''}
                    onChange={e => handleChange('Menor_Nome_Instituicao', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Processo Ativo em Tribunal de Família / CPCJ?</label>
                  <select
                    value={formData.Menor_Processo_CPCJ_Tribunal || ''}
                    onChange={e => handleChange('Menor_Processo_CPCJ_Tribunal', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Com medida aplicada</option>
                    <option value="Não">Não</option>
                    <option value="Desconhecido">Desconhecido</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Número do Processo CPCJ / Tribunal</label>
                  <input
                    type="text"
                    placeholder="Ex: 123/24.5T8SNT"
                    value={formData.Menor_Numero_Processo || ''}
                    onChange={e => handleChange('Menor_Numero_Processo', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Conflito Familiar / Progenitores Recente?</label>
                  <select
                    value={formData.Menor_Conflito_Familiar || ''}
                    onChange={e => handleChange('Menor_Conflito_Familiar', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Discussão grave nas últimas 24h</option>
                    <option value="Não">Não</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Suspeita de Aliciamento Online / Redes Sociais?</label>
                  <select
                    value={formData.Menor_Suspeita_Aliciamento_Online || ''}
                    onChange={e => handleChange('Menor_Suspeita_Aliciamento_Online', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Contactos recentes suspeitos</option>
                    <option value="Não">Não</option>
                    <option value="Desconhecido">Desconhecido</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Histórico de Fugas Anteriores?</label>
                  <select
                    value={formData.Menor_Fugas_Anteriores || ''}
                    onChange={e => handleChange('Menor_Fugas_Anteriores', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Já fugiu anteriormente</option>
                    <option value="Não">Não - Primeiro episódio</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Detalhes das Fugas Anteriores (Locais onde foi encontrado)</label>
                  <input
                    type="text"
                    placeholder="Ex: Foi encontrado na residência de amigos em Lisboa, na estação do Oriente..."
                    value={formData.Menor_Historico_Fugas || ''}
                    onChange={e => handleChange('Menor_Historico_Fugas', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Sub-bloco exclusivo para Adultos */
            <div className="conditional-container adult">
              <div className="conditional-header adult">
                Registo de Adulto (&gt;= 18 anos) - Doutrina de Dinâmica Pessoal PDGNR
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Abandonou Menores ou Dependentes a Cargo?</label>
                  <select
                    value={formData.Adulto_Abandono_Menores_Cargo || formData.Abandonou_Menores_Cargo || ''}
                    onChange={e => {
                      handleChange('Adulto_Abandono_Menores_Cargo', e.target.value);
                      handleChange('Abandonou_Menores_Cargo', e.target.value);
                    }}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Deixou crianças/dependentes sozinhos</option>
                    <option value="Não">Não</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Conflito Conjugal / Separação Litigiosa Recente?</label>
                  <select
                    value={formData.Adulto_Conflito_Conjugal_Separacao || ''}
                    onChange={e => handleChange('Adulto_Conflito_Conjugal_Separacao', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Discussão conjugal recente ou separação</option>
                    <option value="Não">Não</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Dificuldades Financeiras Graves / Dívidas?</label>
                  <select
                    value={formData.Adulto_Dificuldades_Financeiras_Dividas || ''}
                    onChange={e => handleChange('Adulto_Dificuldades_Financeiras_Dividas', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Penhoras, falência ou dívidas avultadas</option>
                    <option value="Não">Não</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Problemas Laborais / Desemprego Recente?</label>
                  <select
                    value={formData.Adulto_Problemas_Laborais_Desemprego || ''}
                    onChange={e => handleChange('Adulto_Problemas_Laborais_Desemprego', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Despedimento ou pressão extrema</option>
                    <option value="Não">Não</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Luto Recente / Perda Afetiva Forte?</label>
                  <select
                    value={formData.Adulto_Luto_Recente_Perda || ''}
                    onChange={e => handleChange('Adulto_Luto_Recente_Perda', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Morte de cônjuge, filho ou progenitor</option>
                    <option value="Não">Não</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Problemas de Álcool / Dependência de Substâncias?</label>
                  <select
                    value={formData.Adulto_Consumo_Substancias_Alcool || ''}
                    onChange={e => handleChange('Adulto_Consumo_Substancias_Alcool', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    <option value="Sim">Sim - Dependência diagnosticada</option>
                    <option value="Não">Não</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* BLOCO 10: Dinâmica Social, Últimos Movimentos e Diligências Iniciais */}
        <section id="sec-diligencias" className="registration-section">
          <h2>10. Dinâmica & Diligências</h2>
          <p className="subtitle">Últimos contactos, rede de relações pessoais, indícios de voluntariedade e primeiras diligências efetuadas no terreno.</p>

          <div className="form-grid">
            <div className="form-group">
              <label>Última Pessoa a Contactar a Vítima</label>
              <input
                type="text"
                placeholder="Nome da pessoa"
                value={formData.Ultimo_Contacto_Pessoa || ''}
                onChange={e => handleChange('Ultimo_Contacto_Pessoa', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Contacto Telefónico Dessa Pessoa</label>
              <input
                type="tel"
                maxLength="9"
                placeholder="9 algarismos"
                value={formData.Ultimo_Contacto_Contacto || ''}
                onChange={e => handleChange('Ultimo_Contacto_Contacto', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Data e Hora Desse Último Contacto</label>
              <input
                type="datetime-local"
                value={formData.Ultimo_Contacto_DataHora || ''}
                onChange={e => handleChange('Ultimo_Contacto_DataHora', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Locais Frequentados Habitualmente</label>
              <input
                type="text"
                placeholder="Ex: Café Central em Sintra, Parque Urbano, Igreja Paroquial, horta da família..."
                value={formData.Locais_Habituais_Frequencia || ''}
                onChange={e => handleChange('Locais_Habituais_Frequencia', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Familiares e Amigos de Referência</label>
              <input
                type="text"
                placeholder="Ex: Irmão António Silva (912345678), Amigo de infância João Vale em Loures..."
                value={formData.Amigos_Familiares_Referencia || ''}
                onChange={e => handleChange('Amigos_Familiares_Referencia', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Indícios de Voluntariedade / Preparação Prévia</label>
              <input
                type="text"
                placeholder="Ex: Deixou carta manuscrita, levou malas de viagem, levantou dinheiro no multibanco..."
                value={formData.Sinais_Voluntariedade || ''}
                onChange={e => handleChange('Sinais_Voluntariedade', e.target.value)}
              />
            </div>

            <div className="form-group full-width">
              <label>Diligências Já Efetuadas pela Patrulha / Denunciante</label>
              <textarea
                placeholder="Ex: Verificado Hospital de Cascais (sem registo de entrada), contacto com a estação de comboios, patrulha efetuou batimento apeado no pinhal envolvente..."
                value={formData.Diligencias_Ja_Efetuadas || ''}
                onChange={e => handleChange('Diligencias_Ja_Efetuadas', e.target.value)}
                rows={3}
              />
            </div>

            <div className="form-group full-width">
              <label>Observações Adicionais e Notas Operacionais <InfoHelper text="O motor de IA SARIA processa este texto na íntegra para afinar o perfil de busca e os setores tácticos." /></label>
              <textarea
                placeholder="Escreva qualquer outro dado relevante para os elementos de busca no terreno..."
                value={formData.Observacoes_Adicionais || ''}
                onChange={e => handleChange('Observacoes_Adicionais', e.target.value)}
                rows={4}
              />
            </div>
          </div>
        </section>

        {/* Action Buttons: Discard and Submit */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-discard"
            onClick={handleDiscardDraft}
            disabled={processing}
          >
            Descartar Rascunho
          </button>

          <button
            type="button"
            className="btn-submit"
            onClick={submitForm}
            disabled={processing}
          >
            {processing ? 'A Submeter Registo Oficial...' : 'Concluir Registo Oficial PDGNR'}
          </button>
        </div>

      </main>
    </div>
  );
}

export default CaseRegistrationOfficial;
